import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import { Box, Button, Grid, Skeleton, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import styled from "styled-components";
import ImageSizingContainer from "../common/ImageSizingContainer";
import ArrowLeft from "./ArrowLeft";
import ArrowRight from "./ArrowRight";
import "./PropertyDetailsCarousel.styles.css";
import { useTranslation } from "react-i18next";

type PropertyDetailsCarouselProps = {
  images: any;
  loading: boolean;
};

const PropertyDetailsCarousel = ({
  images,
  loading,
}: PropertyDetailsCarouselProps) => {
  const [fullscreen, setFullscreen] = useState(false);
  const [carouselImages, setCarouselImages] = useState<any[]>([]);
  const { t } = useTranslation();

  useEffect(() => {
    if (images != null && !loading) {
      setCarouselImages(
        images
          .sort((a: any, b: any) => a.imageOrderPosition - b.imageOrderPosition)
          .map((image: any) => {
            return {
              original: image.link,
              thumbnail: image.link,
            };
          }),
      );
    }
  }, [images, loading]);

  const handleFullscreenChange = (fullscreen: boolean) => {
    setFullscreen(fullscreen);
  };

  return (
    <Grid>
      {loading ? (
        <Skeleton variant="rectangular" width={"100%"} height={520} />
      ) : (
        <>
          <Grid
            style={{
              position: "relative",
            }}
            container
            spacing={1}
          >
            <Grid item xs={8}>
              <ImageSizingContainer
                source={
                  carouselImages[0]?.original
                    ? carouselImages[0]?.original
                    : require("../../assets/images/no-image.jpg")
                }
                alt={"gallery 1"}
                sx={{
                  width: "100%",
                  height: isMobile ? "12rem" : "22rem",
                  borderRadius: "15px",
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <ImageSizingContainer
                source={
                  carouselImages[1]?.original
                    ? carouselImages[1]?.original
                    : require("../../assets/images/no-image.jpg")
                }
                alt={"gallery 2"}
                sx={{
                  width: "100%",
                  height: isMobile ? "5.8rem" : "10.9rem",
                  borderRadius: "15px",
                  marginBottom: ".4rem",
                }}
              />
              <ImageSizingContainer
                source={
                  carouselImages[2]?.original
                    ? carouselImages[2]?.original
                    : require("../../assets/images/no-image.jpg")
                }
                alt={"gallery 2"}
                sx={{
                  width: "100%",
                  height: isMobile ? "5.8rem" : "10.9rem",
                  borderRadius: "15px",
                }}
              />
            </Grid>
            <Button
              style={{
                position: "absolute",
                bottom: "1rem",
                right: "1rem",
                borderRadius: ".5rem",
                backgroundColor: "#fff",
                padding: ".5rem 2rem",
                color: "#000",
              }}
            >
              <PhotoCamera
                style={{
                  verticalAlign: "middle",
                  color: "#666",
                  paddingRight: ".25rem",
                }}
              />
              <Typography variant="body2">{t("details.all_photos")}</Typography>
            </Button>

            {/* Carousel, only to be shown when clicked */}
            <Box
              style={{
                opacity: !fullscreen ? 0 : 1,
                position: "absolute",
                top: 0,
                right: 0,
                width: "100%",
              }}
            >
              <ImageGallery
                items={carouselImages}
                showPlayButton={false}
                showFullscreenButton={true}
                slideDuration={0}
                onScreenChange={handleFullscreenChange}
                stopPropagation={true}
                showThumbnails={fullscreen}
                renderLeftNav={(onClick) => (
                  <LeftNav
                    onClick={onClick}
                    disabled={isMobile ? true : false}
                  />
                )}
                renderRightNav={(onClick) => (
                  <RightNav
                    onClick={onClick}
                    disabled={isMobile ? true : false}
                  />
                )}
                renderFullscreenButton={(onClick, isFullscreen) => (
                  <FullscreenButton
                    onClick={onClick}
                    isFullscreen={isFullscreen}
                  />
                )}
              />
            </Box>
          </Grid>
        </>
      )}
    </Grid>
  );
};

interface NavButtonProps {
  onClick: React.MouseEventHandler;
  disabled: boolean;
}

interface FullscreenButtonProps {
  onClick: React.MouseEventHandler;
  isFullscreen: boolean;
}

const LeftNav = ({ onClick, disabled }: NavButtonProps) => {
  return disabled ? (
    <></>
  ) : (
    <LeftNavButton onClick={onClick}>
      <ArrowLeft />
    </LeftNavButton>
  );
};

const RightNav = ({ onClick, disabled }: NavButtonProps) => {
  return disabled ? (
    <></>
  ) : (
    <RightNavButton onClick={onClick}>
      <ArrowRight />
    </RightNavButton>
  );
};

const FullscreenButton = ({ onClick, isFullscreen }: FullscreenButtonProps) => {
  return isFullscreen ? (
    <CloseFullscreenButton onClick={onClick}>
      <CloseRoundedIcon fontSize="large" />
    </CloseFullscreenButton>
  ) : (
    <OpenFullscreenButton onClick={onClick}></OpenFullscreenButton>
  );
};

const LeftNavButton = styled.button<{
  onClick: any;
  children: React.ReactNode;
}>`
  background: transparent;
  border: none;
  position: absolute;
  left: 5px;
  top: 45%;
  z-index: 100;
  cursor: pointer;
  opacity: 0.6;

  @media (max-width: 600px) {
    top: 40%;
  }
`;

const RightNavButton = styled.button<{
  onClick: any;
  children: React.ReactNode;
}>`
  background: transparent;
  border: none;
  position: absolute;
  right: 5px;
  top: 45%;
  z-index: 100;
  cursor: pointer;
  opacity: 0.6;

  @media (max-width: 600px) {
    top: 40%;
  }
`;

const OpenFullscreenButton = styled.button<{ onClick: any }>`
  position: absolute;
  top: 0;
  background-color: transparent;
  border: none;
  cursor: pointer;
  width: 100%;
  height: 100%;
`;

const CloseFullscreenButton = styled.button<{
  onClick: any;
  children: React.ReactNode;
}>`
  position: absolute;
  top: 0;
  right: 0;
  background-color: transparent;
  border: none;
  cursor: pointer;
  color: #fff;
`;

export default PropertyDetailsCarousel;
